










































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '../../../../store/store';
import { ApiState } from '@/store/types/general.types';
import {
  AddressCountry,
  AddressState,
  AddressFilterOption,
  LocationType
} from '@/store/modules/admin/types/group-level-attribute.types';
import { ToastProgrammatic as Toast } from 'buefy';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { clone } from '@/jbi-shared/util/group-level-attributes.util';
import LocationAttributeRadioSelection from '@/views/JTMS/Dashboard/CreateCourses/CourseLevelAttributes/components/LocationAttributeRadioSelection.vue';
import { isLink } from '@/jbi-shared/util/validate-email-domains.util';
import {
  GroupLevelAttributeOption,
  GroupLevelAttributeOptionStatus,
  GroupLevelAttributeValue,
  GroupLevelAttributeValueStatus
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { deepCopy } from '@/jbi-shared/util/deep-copy.util';

@Component({
  components: {
    LocationAttributeRadioSelection
  }
})
export default class GroupLevelAttributeLocationField extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop() fieldClasses!: any;
  @Prop() duplicationError!: boolean;
  @Prop({ default: false }) isSettings!: boolean;
  @Prop({ default: false }) isFromAutoSave!: boolean;
  @Prop() deleteAttribute!: () => void;
  @Prop() updateAttributeValue!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() toggleAttributeRequire!: (
    attribute: GroupLevelAttributeValue
  ) => void;
  @Prop() editAttribute!: (attribute: GroupLevelAttributeValue) => void;

  // Defaults to 'Venue' option
  linkErrorMsg: string = '';
  isLinkFormatCorrect: boolean = true;

  lineOneErrorMsg: string = '';
  cityErrorMsg: string = '';
  postcodeErrorMsg: string = '';
  stateErrorMsg: string = '';
  countryErrorMsg: string = '';

  currentCountry: AddressCountry | undefined = undefined;
  currentStates: AddressState[] = [];
  selectedCountryId: number = 0; // used to determine whether to store fetched states
  isStateless: boolean = true;

  lineOneFieldError: boolean = true;
  cityFieldError: boolean = true;
  postcodeFieldError: boolean = true;
  countryFieldError: boolean = true;
  stateFieldError: boolean = true;
  linkFieldError: boolean = true;

  // Shorthand referenced for rendering only
  locationType: GroupLevelAttributeOption | undefined = undefined;

  localBuildingValue: string = '';
  localLineOneValue: string = '';
  localLineTwoValue: string = '';
  localCityValue: string = '';
  localPostcodeValue: string = '';
  localStateValue: string = '';
  localCountryValue: string = '';
  localLinkValue: string = '';
  localLocationTypeValue: LocationType = LocationType.VENUE; // Default or initial

  @Action('admin/getCountries')
  getAllCountries!: (addressFilter?: AddressFilterOption) => void;

  @State(({ admin }: RootState) => admin.apiState.getCountries)
  getAllCountriesApiState!: ApiState;

  @State(({ admin }: RootState) => admin.countries)
  allCountries!: AddressCountry[] | undefined;

  @Action('admin/getCountryByCountryId')
  getCountryById!: (countryId?: number) => void;

  @Action('course/storeLocationAttributeConfig')
  storeLocationAttributeConfig!: (payload: GroupLevelAttributeOption[]) => void;

  @State(({ admin }: RootState) => admin.apiState.getCountryById)
  getCountryByIdApiState!: ApiState;

  @State(({ admin }: RootState) => admin.countryById)
  countryById!: AddressCountry | undefined;

  @State(
    ({ course }: RootState) =>
      course.courseLevelAttributeState.locationGLAConfig
  )
  locationConfigs!: GroupLevelAttributeOption[];

  mounted() {
    this.syncPropToLocalData();
    this.validateRequired();

    if (this.allCountries === undefined) {
      this.getAllCountries();
    }

    // The correct location type should be pre-selected during tab switches/course settings
    if (this.locationType) {
      this.updateLocationConfig(this.locationType.value as LocationType);
    }
  }

  // Computed property so changes from the value from parent component are synced
  get isRequired() {
    return this.attributeValue?.groupLevelAttributeSpec?.isRequired || false;
  }

  get attributeValueAsGLAOptionArray(): GroupLevelAttributeOption[] {
    if (!this.attributeValue?.value) {
      return (
        (this.attributeValue?.groupLevelAttributeType
          ?.option as GroupLevelAttributeOption[]) || []
      );
    }
    return this.attributeValue.value as GroupLevelAttributeOption[];
  }

  /* Location configs */
  get attributeOptionConfigs(): GroupLevelAttributeOption[] {
    return (this.attributeValue?.option as GroupLevelAttributeOption[]) || [];
  }

  private getOptionSafe(index: number): GroupLevelAttributeOption {
    if (this.locationConfigs && this.locationConfigs[index]) {
      return this.locationConfigs[index];
    }
    return (
      this.attributeOptionConfigs[index] || {
        value: '',
        placeholder: '',
        status: 'INACTIVE'
      }
    );
  }

  get buildingOptions() {
    return this.getOptionSafe(0);
  }

  get lineOneOptions() {
    return this.getOptionSafe(1);
  }

  get lineTwoOptions() {
    return this.getOptionSafe(2);
  }

  get cityOptions() {
    return this.getOptionSafe(3);
  }

  get postcodeOptions() {
    return this.getOptionSafe(4);
  }

  get stateOptions() {
    return this.getOptionSafe(5);
  }

  get countryOptions() {
    return this.getOptionSafe(6);
  }

  get linkOptions() {
    return this.getOptionSafe(7);
  }

  get isRemote() {
    return this.localLocationTypeValue === LocationType.REMOTE;
  }

  get countriesList(): AddressCountry[] {
    if (this.allCountries) {
      return this.allCountries.filter((country) => {
        return country.name
          .toLowerCase()
          .includes((this.localCountryValue as string).toLowerCase());
      });
    }

    return [];
  }

  get isStatelessComputed() {
    return this.isStateless;
  }

  get statesList(): AddressState[] {
    if (this.currentStates.length) {
      return this.currentStates.filter((state) => {
        return state.name
          .toLowerCase()
          .includes((this.localStateValue as string).toLowerCase());
      });
    }

    return [];
  }

  // Central method to emit updates
  emitUpdate() {
    if (!this.attributeValue) return;
    const updatedAttribute = this.buildAttributeValueFromLocalState();
    this.updateAttributeValue(updatedAttribute);
  }

  /**
   * Helper method to ensure value is an array and has the expected structure (9 items), otherwise
   * use default options derived from GLA type options.
   *
   * FIXME: Ideally, perform data sanitization in backend for faulty GLA values, but seeing as this
   * GLA is only used as a default GLA in the context of JTMS courses, doing it from the frontend is
   * manageable.
   */
  getAttributeValueAsArray(): GroupLevelAttributeOption[] {
    if (
      Array.isArray(this.attributeValue.value) &&
      this.attributeValue.value.length >= 9
    ) {
      return this.attributeValue.value as GroupLevelAttributeOption[];
    } else if (
      Array.isArray(this.attributeValue.groupLevelAttributeType.option)
    ) {
      /**
       * Fallback to default options structure if value is missing/invalid
       * This also ensures the default options have a 'value' property for consistency
       */
      return (this.attributeValue.groupLevelAttributeType
        .option as GroupLevelAttributeOption[]).map((opt) => ({
        ...opt,
        value: ''
      }));
    }
    // Absolute fallback - should ideally not happen if props are correct
    return Array(9).fill({ value: '', placeholder: '', status: 'ACTIVE' });
  }

  syncPropToLocalData() {
    const propValueArray = this.getAttributeValueAsArray();

    this.localBuildingValue = (propValueArray[0]?.value as string) || '';
    this.localLineOneValue = (propValueArray[1]?.value as string) || '';
    this.localLineTwoValue = (propValueArray[2]?.value as string) || '';
    this.localCityValue = (propValueArray[3]?.value as string) || '';
    this.localPostcodeValue = (propValueArray[4]?.value as string) || '';
    this.localStateValue = (propValueArray[5]?.value as string) || '';
    this.localCountryValue = (propValueArray[6]?.value as string) || '';
    this.localLinkValue = (propValueArray[7]?.value as string) || '';
    this.localLocationTypeValue = propValueArray[8]?.value as LocationType;
    this.locationType = propValueArray[8];

    // Fetch country details if a country is pre-selected
    if (this.localCountryValue && this.allCountries) {
      this.verifyCountryInput(); // Trigger fetch if needed
    } else if (this.localCountryValue) {
      // If countries aren't loaded yet, watch for them
      const unwatch = this.$watch('allCountries', (newCountries) => {
        if (newCountries && newCountries.length > 0) {
          this.verifyCountryInput();
          unwatch(); // Stop watching once loaded
        }
      });
    }

    // Set initial location type radio button state, do not emit on intial sync
    this.updateLocationConfig(
      this.localLocationTypeValue as LocationType,
      false
    );
  }

  // Helper to construct the full attribute object from local state
  buildAttributeValueFromLocalState(): GroupLevelAttributeValue {
    const propArray = this.getAttributeValueAsArray();
    const updatedValueArray: GroupLevelAttributeOption[] = clone(propArray);

    // Update values from local state
    if (updatedValueArray[0])
      updatedValueArray[0].value = this.localBuildingValue;
    if (updatedValueArray[1])
      updatedValueArray[1].value = this.localLineOneValue;
    if (updatedValueArray[2])
      updatedValueArray[2].value = this.localLineTwoValue;
    if (updatedValueArray[3]) updatedValueArray[3].value = this.localCityValue;
    if (updatedValueArray[4])
      updatedValueArray[4].value = this.localPostcodeValue;
    if (updatedValueArray[5]) updatedValueArray[5].value = this.localStateValue;
    if (updatedValueArray[6])
      updatedValueArray[6].value = this.localCountryValue;
    if (updatedValueArray[7]) updatedValueArray[7].value = this.localLinkValue;
    if (updatedValueArray[8])
      updatedValueArray[8].value = this.localLocationTypeValue;

    // Create a new object to emit
    const newAttributeValue: GroupLevelAttributeValue = {
      ...clone(this.attributeValue),
      value: updatedValueArray,
      hasFieldError:
        this.fieldErrorMessages().some((msg) => msg && msg.length > 0) ||
        !this.isLinkFormatCorrect
    };

    return newAttributeValue;
  }

  setLocationFieldStatus(
    clonedOptions: GroupLevelAttributeOption[],
    indices: number[],
    status: GroupLevelAttributeOptionStatus
  ): void {
    indices.forEach((index) => {
      if (clonedOptions[index]) {
        clonedOptions[index].status = status;
      }
    });
  }

  setLocationTypeValue(
    clonedValue: GroupLevelAttributeOption[],
    locationType: LocationType
  ) {
    if (clonedValue && clonedValue[8]) {
      clonedValue[8].value = locationType;
    }
  }

  /**
   * Helper method to dynamically hide/show venue/remote fields for location GLA
   * based on selected location type.
   * @param locationType Location Type emitted from radio button component
   * @param shouldEmitUpdates Flag to determine whether to clear certain field
   * values when switching between location types
   */
  updateLocationConfig(
    locationType: LocationType,
    shouldEmitUpdates: boolean = true
  ): void {
    this.localLocationTypeValue = locationType;
    const clonedAttributeValue: GroupLevelAttributeValue = deepCopy(
      this.attributeValue
    );

    if (!clonedAttributeValue) return;

    const clonedOptions: GroupLevelAttributeOption[] =
      (clonedAttributeValue.option as GroupLevelAttributeOption[]) || [];
    const clonedValues: GroupLevelAttributeOption[] =
      (clonedAttributeValue.value as GroupLevelAttributeOption[]) || [];

    const venueConfigIndex: number[] = [0, 1, 2, 3, 4, 5, 6];
    const remoteConfigIndex: number = 7;

    switch (locationType) {
      case LocationType.VENUE:
        this.setLocationFieldStatus(
          clonedOptions,
          venueConfigIndex,
          GroupLevelAttributeOptionStatus.ACTIVE
        );
        this.setLocationFieldStatus(
          clonedOptions,
          [remoteConfigIndex],
          GroupLevelAttributeOptionStatus.DELETE
        );

        this.setLocationFieldStatus(
          clonedValues,
          venueConfigIndex,
          GroupLevelAttributeOptionStatus.ACTIVE
        );
        this.setLocationFieldStatus(
          clonedValues,
          [remoteConfigIndex],
          GroupLevelAttributeOptionStatus.DELETE
        );

        // Clear remote field value when switching away
        if (shouldEmitUpdates) {
          // Reset local values
          this.resetLocalLinkValue();
        }

        this.setLocationTypeValue(clonedValues, locationType);
        break;

      case LocationType.REMOTE:
        this.setLocationFieldStatus(
          clonedOptions,
          venueConfigIndex,
          GroupLevelAttributeOptionStatus.DELETE
        );
        this.setLocationFieldStatus(
          clonedOptions,
          [remoteConfigIndex],
          GroupLevelAttributeOptionStatus.ACTIVE
        );

        this.setLocationFieldStatus(
          clonedValues,
          venueConfigIndex,
          GroupLevelAttributeOptionStatus.DELETE
        );
        this.setLocationFieldStatus(
          clonedValues,
          [remoteConfigIndex],
          GroupLevelAttributeOptionStatus.ACTIVE
        );

        // Clear venue field values when switching away
        if (shouldEmitUpdates) {
          // Reset local values
          this.resetLocalVenueValues();
          this.resetCountryStateDependents();
        }

        this.setLocationTypeValue(clonedValues, locationType);
        break;

      case LocationType.BOTH:
        this.setLocationFieldStatus(
          clonedOptions,
          venueConfigIndex,
          GroupLevelAttributeOptionStatus.ACTIVE
        );
        this.setLocationFieldStatus(
          clonedOptions,
          [remoteConfigIndex],
          GroupLevelAttributeOptionStatus.ACTIVE
        );

        this.setLocationFieldStatus(
          clonedValues,
          venueConfigIndex,
          GroupLevelAttributeOptionStatus.ACTIVE
        );
        this.setLocationFieldStatus(
          clonedValues,
          [remoteConfigIndex],
          GroupLevelAttributeOptionStatus.ACTIVE
        );

        this.setLocationTypeValue(clonedValues, locationType);
        break;

      default:
        // Enable all venue fields by default
        this.setLocationFieldStatus(
          clonedOptions,
          venueConfigIndex,
          GroupLevelAttributeOptionStatus.ACTIVE
        );
        this.setLocationFieldStatus(
          clonedOptions,
          [remoteConfigIndex],
          GroupLevelAttributeOptionStatus.DELETE
        );

        this.setLocationFieldStatus(
          clonedValues,
          venueConfigIndex,
          GroupLevelAttributeOptionStatus.ACTIVE
        );
        this.setLocationFieldStatus(
          clonedValues,
          [remoteConfigIndex],
          GroupLevelAttributeOptionStatus.DELETE
        );

        this.setLocationTypeValue(clonedValues, locationType);
        break;
    }

    // Update the attribute value with modified options
    clonedAttributeValue.option = clonedOptions;
    this.storeLocationAttributeConfig(clonedOptions);
    clonedAttributeValue.value = clonedValues;
    this.updateAttributeValue(clonedAttributeValue);
  }

  /**
   * DO NOT convert this method into a getter.
   * The error message variables for each field are updated
   * asynchronously via a watcher after a user input is
   * registered. A getter might try to access it before the
   * value is updated correctly. That wouldn't be very stonks.
   */
  fieldErrorMessages(): string[] {
    return [
      this.lineOneErrorMsg,
      this.cityErrorMsg,
      this.postcodeErrorMsg,
      this.stateErrorMsg,
      this.countryErrorMsg
    ];
  }

  validateLinkField(): void {
    this.isLinkFormatCorrect = false;
    this.resetLinkErrorMessage();
    this.setLinkFieldError(false);

    // Guard Clause: Only validate if the link field is active
    if (this.linkOptions?.status !== GroupLevelAttributeValueStatus.ACTIVE) {
      this.linkErrorMsg = '';
      this.isLinkFormatCorrect = true;
      this.linkFieldError = false;
      return;
    }

    const hasValue = this.localLinkValue;

    // Case 1: Required and Empty
    if (this.isRequired && !hasValue) {
      this.linkErrorMsg = `${this.linkOptions.placeholder} is required.`;
      this.isLinkFormatCorrect = false;
      this.linkFieldError = true;
      return;
    }

    // Case 2: Has value but invalid format
    if (hasValue && !isLink(hasValue)) {
      this.linkErrorMsg = "Invalid link format. Use 'https://' or 'http://'.";
      this.isLinkFormatCorrect = false;
      this.linkFieldError = true;
      return;
    }

    // Case 3: Valid (or not required and empty)
    this.linkErrorMsg = '';
    this.isLinkFormatCorrect = true;
    this.linkFieldError = false;
  }

  /**
   * Check for country name when user types manually.
   * Assign corrected value (string case) if input matched
   */
  verifyCountryInput(countryInput?: string): void {
    // Guard clause 1: Field inactive
    if (this.countryOptions?.status !== GroupLevelAttributeValueStatus.ACTIVE) {
      this.countryErrorMsg = '';
      this.resetCountryStateDependents(); // Clear related state info
      return;
    }

    // Guard clause 2: Data not ready, should not validate yet
    if (!this.allCountries) {
      this.countryErrorMsg = '';
      // Do not reset dependents here, wait for data
      this.countryFieldError = false;
      return;
    }

    const hasValue =
      countryInput !== undefined ? countryInput : this.localCountryValue;

    /**
     * Early return on missing value, if country becomes empty, reset states
     * and re-verify state (which will clear field error too)
     */
    if (!hasValue) {
      this.countryErrorMsg = this.isRequired
        ? `${this.countryOptions.placeholder} is required.`
        : '';
      this.resetCountryStateDependents();
      this.verifyStateInput();
      this.countryFieldError = true;
      return;
    }

    // Continue validation for non-empty values
    const targetCountry = this.allCountries.find(
      (country) => country.name.toLowerCase() === hasValue.toLowerCase()
    );

    // Case 1: Invalid country
    if (!targetCountry) {
      this.countryErrorMsg = 'Invalid country name.';
      this.resetCountryStateDependents();
      this.verifyStateInput();
      this.countryFieldError = true;
      return;
    }

    // Case 2: Valid country
    this.countryErrorMsg = '';
    const previousSelectedCountryId = this.selectedCountryId;
    this.localCountryValue = targetCountry.name;
    this.selectedCountryId = targetCountry.id;
    this.countryFieldError = false;

    // Fetch states only if the country ID actually changed
    if (this.selectedCountryId !== previousSelectedCountryId) {
      this.getCountryById(this.selectedCountryId);
      // Clear local state value only if triggered by user input/change, not initial sync
      if (countryInput !== undefined) {
        this.localStateValue = '';
        // Explicitly mark as potentially stateless until fetch completes
        this.isStateless = true;
        this.currentStates = [];
      }
    }

    // Always re-verify state after country is validated (or changed)
    this.verifyStateInput();
  }

  /**
   * Verifies manual input and update case to exact match
   */
  verifyStateInput(stateInput?: string): void {
    /**
     * Guard clause 1:
     * Ensure field is active and associated country has states.
     * Also ensures `currentStates` is available (should be if not stateless, but check defensively)
     * on the off-chance API call has not returned data.
     */
    if (
      this.stateOptions?.status !== GroupLevelAttributeValueStatus.ACTIVE ||
      this.isStateless ||
      !this.currentStates ||
      this.currentStates.length === 0
    ) {
      this.stateErrorMsg = '';
      this.stateFieldError = false;
      return;
    }

    const hasValue =
      stateInput !== undefined ? stateInput : this.localStateValue;

    // Early return on missing value
    if (!hasValue) {
      this.stateErrorMsg = this.isRequired
        ? `${this.stateOptions.placeholder} is required.`
        : '';
      this.stateFieldError = true;
      return;
    }

    // Continue validation for non-empty values
    const targetState = this.currentStates.find(
      (state) => state.name.toLowerCase() === hasValue.toLowerCase()
    );

    // Case 1: Invalid state
    if (!targetState) {
      this.stateErrorMsg = 'Invalid state name.';
      this.stateFieldError = true;
      return;
    }

    // Case 2: Valid state
    this.stateErrorMsg = '';
    this.localStateValue = targetState.name;
    this.stateFieldError = false;
  }

  verifyLineOneInput(lineOneInput?: string): void {
    if (this.lineOneOptions?.status !== GroupLevelAttributeValueStatus.ACTIVE) {
      this.lineOneErrorMsg = '';
      this.lineOneFieldError = false;
      return;
    }

    const hasValue =
      lineOneInput !== undefined ? lineOneInput : this.localLineOneValue;

    if (this.isRequired && !hasValue) {
      this.lineOneErrorMsg = `${this.lineOneOptions.placeholder} is required.`;
      this.lineOneFieldError = true;
      return;
    }

    this.lineOneErrorMsg = '';
    this.lineOneFieldError = false;
  }

  verifyCityInput(cityInput?: string): void {
    if (this.cityOptions?.status !== GroupLevelAttributeValueStatus.ACTIVE) {
      this.cityErrorMsg = '';
      this.cityFieldError = false;
      return;
    }
    const hasValue = cityInput !== undefined ? cityInput : this.localCityValue;

    if (this.isRequired && !hasValue) {
      this.cityErrorMsg = `${this.cityOptions.placeholder} is required.`;
      this.cityFieldError = true;
      return;
    }

    this.cityErrorMsg = '';
    this.cityFieldError = false;
  }

  verifyPostcodeInput(postcodeInput?: string): void {
    if (
      this.postcodeOptions?.status !== GroupLevelAttributeValueStatus.ACTIVE
    ) {
      this.postcodeErrorMsg = '';
      this.postcodeFieldError = false;
      return;
    }
    const hasValue =
      postcodeInput !== undefined ? postcodeInput : this.localPostcodeValue;

    if (this.isRequired && !hasValue) {
      this.postcodeErrorMsg = `${this.postcodeOptions.placeholder} is required.`;
      this.postcodeFieldError = true;
      return;
    }

    this.postcodeErrorMsg = '';
    this.postcodeFieldError = false;
  }

  updateErrorState(): boolean {
    this.attributeValue.hasFieldError =
      this.fieldErrorMessages().join('').length > 0;
    return this.attributeValue.hasFieldError;
  }

  assignCountryValue(selectedCountry: AddressCountry | null): void {
    this.localCountryValue = selectedCountry ? selectedCountry.name : '';
    this.verifyCountryInput(this.localCountryValue);
  }

  assignStateValue(selectedState: AddressState | null): void {
    this.localStateValue = selectedState ? selectedState.name : '';
    this.verifyStateInput(this.localStateValue);
  }

  validateAllFields(): void {
    // // Reset all potential errors first
    this.isLinkFormatCorrect = true;
    this.resetAllFieldErrorMessages();
    this.setAllVenueFieldErrors(false);
    this.setLinkFieldError(false);

    this.validateVenueFields();
    this.validateLinkField();
  }

  setFieldError(value: boolean) {
    this.attributeValue.hasFieldError = value;
  }

  setAllVenueFieldErrors(value: boolean) {
    this.lineOneFieldError = value;
    this.cityFieldError = value;
    this.postcodeFieldError = value;
    this.countryFieldError = value;
    this.stateFieldError = value;
  }

  setLinkFieldError(value: boolean) {
    this.linkFieldError = value;
  }

  resetLocalVenueValues(): void {
    this.localBuildingValue = '';
    this.localLineOneValue = '';
    this.localLineTwoValue = '';
    this.localCityValue = '';
    this.localPostcodeValue = '';
    this.localStateValue = '';
    this.localCountryValue = '';
  }

  resetLocalLinkValue(): void {
    this.localLinkValue = '';
  }

  // Helper to reset state fields dependent on the country
  resetCountryStateDependents(): void {
    this.selectedCountryId = 0;
    this.isStateless = true;
    this.currentStates = [];
    // Clear local state value only if it currently has a value to avoid unnecessary watcher triggers/emits
    if (this.localStateValue) {
      this.localStateValue = '';
    }
  }

  resetAllFieldErrorMessages() {
    this.resetVenueFieldErrorMessages();
    this.resetLinkErrorMessage();
  }

  resetVenueFieldErrorMessages() {
    this.lineOneErrorMsg = '';
    this.cityErrorMsg = '';
    this.postcodeErrorMsg = '';
    this.stateErrorMsg = '';
    this.countryErrorMsg = '';
  }

  resetLinkErrorMessage() {
    this.linkErrorMsg = '';
  }

  // Helper to group venue field validation
  validateVenueFields(): void {
    this.resetVenueFieldErrorMessages();
    this.setAllVenueFieldErrors(false);

    this.verifyLineOneInput();
    this.verifyCityInput();
    this.verifyPostcodeInput();
    // Country validation might trigger state fetch, so call it first
    this.verifyCountryInput();
    // State validation depends on country's status (stateless or not)
    this.verifyStateInput();
  }

  @isTruthy
  @Watch('getAllCountriesApiState.error')
  @Watch('getCountryByIdApiState.error')
  addressApiErrorCallback(): void {
    Toast.open('Error fetching countries and states.');
  }

  @Watch('getAllCountriesApiState.success')
  validateCountryCallback(): void {
    // Clear state value when there's error with country name
    if (this.countryErrorMsg) {
      this.localStateValue = '';
    }
  }

  @isTruthy
  @Watch('getCountryByIdApiState.success')
  validateStateCallback(): void {
    if (
      this.countryById &&
      (this.selectedCountryId === 0 ||
        this.selectedCountryId === this.countryById.id)
    ) {
      this.currentCountry = clone(this.countryById) as AddressCountry;
      this.currentStates = clone(this.countryById.states) as AddressState[];
      this.isStateless = this.currentStates.length === 0;
      // Re-validate state after fetching states
      this.verifyStateInput();
    }
  }

  // Watchers for local address field changes to trigger validation and emit updates
  @Watch('localBuildingValue')
  @Watch('localLineOneValue')
  @Watch('localLineTwoValue')
  @Watch('localCityValue')
  @Watch('localPostcodeValue')
  @Watch('localStateValue')
  @Watch('localCountryValue')
  @Watch('localLinkValue')
  onLocalDataChange() {
    // Emit the updated object
    this.emitUpdate();
  }

  // Building and line two fields are ALWAYS optional even if GLA itself is required.
  @Watch('lineOneFieldError')
  @Watch('cityFieldError')
  @Watch('postcodeFieldError')
  @Watch('countryFieldError')
  @Watch('stateFieldError')
  @Watch('linkFieldError')
  onIndividualFieldErrorStates(): void {
    let hasFieldError: boolean = false;

    switch (this.localLocationTypeValue) {
      case LocationType.VENUE:
        hasFieldError =
          this.lineOneFieldError ||
          this.cityFieldError ||
          this.postcodeFieldError ||
          this.countryFieldError ||
          this.stateFieldError;
        break;
      case LocationType.REMOTE:
        hasFieldError = this.linkFieldError;
        break;

      default:
        hasFieldError =
          this.lineOneFieldError ||
          this.cityFieldError ||
          this.postcodeFieldError ||
          this.countryFieldError ||
          this.stateFieldError ||
          this.linkFieldError;
        break;
    }

    this.setFieldError(hasFieldError);
  }

  @Watch('isRequired')
  @Watch('localLocationTypeValue')
  validateRequired() {
    if (!this.attributeValue) return;
    const clonedCurrentValues: GroupLevelAttributeOption[] = deepCopy(
      this.getAttributeValueAsArray()
    );

    // Removes the location type from the values array, we are only
    // concerned with actual field values
    const fieldValues = clonedCurrentValues.slice(0, -1);

    // Only run validation if values are present in the fields
    switch (this.localLocationTypeValue) {
      case LocationType.REMOTE:
        const hasLinkValue = fieldValues[7] && fieldValues[7].value !== '';
        if (hasLinkValue) {
          this.validateLinkField();
          return;
        }
        // Do nothing if no values are found
        break;

      case LocationType.VENUE:
        const hasVenueValues = fieldValues
          .slice(0, -1)
          .some(
            (item) =>
              item &&
              item.status === GroupLevelAttributeValueStatus.ACTIVE &&
              item.value !== ''
          );
        if (hasVenueValues) {
          this.validateVenueFields();
          return;
        }
        // Do nothing if no values are found
        break;

      default:
        const everyFieldHasValues: boolean = clonedCurrentValues.every(
          (item) => item && item.value !== ''
        );

        // Only run validation on all fields if EVERY field has values
        if (everyFieldHasValues) {
          this.validateAllFields();
          return;
        }
        break; // Exit switch case
    }

    // Only update error state
    this.attributeValue.hasFieldError = this.isRequired;
    this.updateAttributeValue(this.attributeValue);
  }
}
